import { Injectable, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { of, switchMap } from 'rxjs';

@Injectable()
export class WebPageTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private readonly titleKey = signal<string | null>(null);

  constructor() {
    super();
    // Reactive to both route changes (titleKey) and a hot language switch: the user-menu language
    // picker changes the active Transloco language without navigating, so `selectTranslate` (not a
    // one-shot `translate()`) is what keeps the tab title in sync in that case.
    const pageName = toSignal(
      toObservable(this.titleKey).pipe(
        switchMap((key) => (key ? this.transloco.selectTranslate(key) : of(null))),
      ),
      { initialValue: null },
    );
    effect(() => {
      const name = pageName();
      this.titleService.setTitle(name ? `${name} - Uvet` : 'Uvet');
    });
  }

  override getResolvedTitleForRoute(snapshot: ActivatedRouteSnapshot): unknown {
    return snapshot.data['titleKey'];
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.titleKey.set(this.buildTitle(snapshot) ?? null);
  }
}
